import React, { useRef, useState } from 'react';
import { finishLoading, startLoading } from 'store/loading/actions';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';

import apiOffers from 'api/offers';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';
import { OfferCurrencyProvider } from 'services/offers/currency';

import formatToApiFormat from './src/offerProduct/formatToApiFormat';
import getDefaultFormState from './src/offerProduct/getDefaultFormState';
import IFormState from './src/offerProduct/IFormState';
import OfferProduct from './src/offerProduct/OfferProduct';
import { useUsedPositions } from './src/offerProduct/usedPositions';

const CreateOfferProduct = (): JSX.Element => {
  const { search } = useLocation();
  // ?priceList=true adds a row of the additional price list instead of a position
  const [formState, setFormState] = useState<IFormState>(() => ({
    ...getDefaultFormState(),
    isPriceListItem: queryString.parse(search).priceList === 'true',
  }));

  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation([translations.common, translations.offers]);
  // A save is on its way to the server
  const saving = useRef(false);
  const { id, type }: { id: string | undefined; type: string } = useParams();
  let productID = '';

  const usedPositions = useUsedPositions(
    id === undefined ? null : parseInt(id, 10),
    type === 'additional-offer'
  );

  const cancel = (calculationWindowOpen?: boolean) => {
    if (calculationWindowOpen === true) {
      history.push(
        `/offers/products/${productID}/${type}?calculationWindowOpen=${true}`
      );
    } else {
      history.push(`/offers/${id}/${type}`);
    }
  };

  const save = (calculationWindowOpen: boolean) => {
    // A second click while saving would create the position twice
    if (id === undefined || saving.current) {
      return;
    }

    saving.current = true;
    const postProduct = async () => {
      dispatch(startLoading('offer-product-post'));

      try {
        const response =
          type === 'additional-offer'
            ? await apiOffers.products.postAdditionalOffer(
                parseInt(id, 10),
                formatToApiFormat(formState)
              )
            : await apiOffers.products.post(
                parseInt(id, 10),
                formatToApiFormat(formState)
              );
        if (response) {
          productID = String(response.id);
        }
      } catch (error) {
        console.log(error);
        dispatch(
          addMessage(
            error?.response?.status === 400
              ? t(`${translations.common}:Fill all fields`)
              : t(`${translations.offers}:positionErrors.Save failed`)
          )
        );
        return;
      } finally {
        dispatch(finishLoading('offer-product-post'));
        saving.current = false;
      }

      cancel(calculationWindowOpen);
    };
    postProduct();
  };

  return (
    <OfferCurrencyProvider
      additionalOfferId={
        id !== undefined && type === 'additional-offer'
          ? parseInt(id, 10)
          : null
      }
      offerId={
        id !== undefined && type !== 'additional-offer'
          ? parseInt(id, 10)
          : null
      }
    >
      <OfferProduct
        action="create"
        cancel={cancel}
        formState={formState}
        locked={false}
        save={save}
        setFormState={setFormState}
        usedPositions={usedPositions}
      />
    </OfferCurrencyProvider>
  );
};

export default CreateOfferProduct;
